<script setup lang="ts" generic="T extends { id: string }">
import type { PropType } from 'vue';

import { ChevronLeft, ChevronRight } from 'lucide-vue-next';
import { computed, onMounted, onUnmounted, ref } from 'vue';

import CarouselDesktop from '@/components/carousel/CarouselDesktop.vue';
import CarouselMobile from '@/components/carousel/CarouselMobile.vue';

defineProps({
  items: {
    type: Array as PropType<Array<T>>,
    required: true,
  },
  minItemWidth: {
    type: Number,
    required: true,
  },
  maxItemWidth: {
    type: Number,
    required: true,
  },
  gap: {
    type: Number,
    default: 16,
  },
});

const BREAKPOINT = 1024;

const $main = ref<HTMLElement>();
const $mobileCarousel = ref<any>();
const carouselSize = ref(0);
const windowSize = ref(0);
let observer: ResizeObserver | undefined;

const carouselComponent = computed(() => {
  if (carouselSize.value < 1024) {
    return CarouselMobile;
  }
  else {
    return CarouselDesktop;
  }
});

const isDisplayingMobileOnDesktop = computed(() => {
  return (carouselSize.value < BREAKPOINT) && (windowSize.value >= BREAKPOINT);
});

function updateCarouselSize() {
  const main = $main.value;
  if (main == null)
    return;
  carouselSize.value = main.getBoundingClientRect().width;
  windowSize.value = document.documentElement?.clientWidth;
}

onMounted(() => {
  const main = $main.value;
  if (main == null) {
    throw new Error('Carousel root is null');
  }
  updateCarouselSize();
  observer = new ResizeObserver(updateCarouselSize);
  observer.observe(main);
});

onUnmounted(() => {
  observer?.disconnect();
});
</script>

<template>
  <div ref="$main" style="position:relative;">
    <button
      v-if="isDisplayingMobileOnDesktop"
      class="carousel-nav-btn carousel-nav-btn--left"
      @click="$mobileCarousel?.scroll('left')"
    >
      <ChevronLeft class="w-4 h-4" />
    </button>

    <div class="carousel-root yb-preset" :style="isDisplayingMobileOnDesktop ? '' : ''">
      <component
        :is="carouselComponent" ref="$mobileCarousel" :items :min-item-width :max-item-width :gap
        :display-scrollbar="isDisplayingMobileOnDesktop" :style="$attrs?.style"
      >
        <template #item="{ item }">
          <slot name="item" :item="item">
            item
          </slot>
        </template>
      </component>
    </div>

    <button
      v-if="isDisplayingMobileOnDesktop"
      class="carousel-nav-btn carousel-nav-btn--right"
      @click="$mobileCarousel?.scroll('right')"
    >
      <ChevronRight class="w-4 h-4" />
    </button>
  </div>
</template>

<style scoped>
.carousel-nav-btn {
  display: none;
  position: absolute;
  top: 50%;
  translate: 0 -50%;
  z-index: 10;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border-radius: 50%;
  background-color: white;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  cursor: pointer;
  transition: background-color 150ms ease;
}

.carousel-nav-btn:hover {
  background-color: #f9fafb;
}

.carousel-nav-btn--left {
  left: 0;
  translate: -50% -50%;
}

.carousel-nav-btn--right {
  right: 0;
  translate: 50% -50%;
}

@media (min-width: 1024px) {
  .carousel-nav-btn {
    display: flex;
  }
}

.carousel-nav-btn > :deep(svg) {
  width: 1rem;
  height: 1rem;
}
</style>
